import React from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import {
  Rocket,
  Building2,
  CreditCard,
  HelpCircle,
  Headphones,
  Scale,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const docNavItems = [
  { name: 'Getting Started', path: '/documentation/getting-started', icon: Rocket },
  { name: 'Listing your school', path: '/documentation/listing-school', icon: Building2 },
  { name: 'Payment terms', path: '/documentation/payment-terms', icon: CreditCard },
  { name: 'FAQ', path: '/documentation/faq', icon: HelpCircle },
  { name: 'Support', path: '/documentation/support', icon: Headphones },
]

const legalNavItem = {
  name: 'Terms & Conditions',
  path: '/documentation/terms-and-conditions',
  icon: Scale,
}

function DocNavLink({
  item,
  isActive,
  compact,
}: {
  item: (typeof docNavItems)[number] | typeof legalNavItem
  isActive: boolean
  compact?: boolean
}) {
  const Icon = item.icon
  if (compact) {
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
          isActive ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-200'
        }`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        {item.name}
      </Link>
    )
  }
  return (
    <Link
      to={item.path}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} />
      {item.name}
    </Link>
  )
}

const DocumentationLayout: React.FC = () => {
  const { user, selectedSchool } = useAuth()
  const location = useLocation()

  return (
    <div className="flex flex-col md:flex-row min-h-screen -m-8 -mt-20 overflow-hidden">
      {/* Main Content - full width on mobile */}
      <main className="flex-1 overflow-y-auto min-h-0 w-full">
        {/* Mobile: horizontal doc nav */}
        <nav className="md:hidden flex gap-2 p-3 overflow-x-auto border-b border-gray-200 bg-gray-50/80 shrink-0">
          {docNavItems.map((item) => (
            <DocNavLink key={item.name} item={item} isActive={location.pathname === item.path} compact />
          ))}
          <DocNavLink
            item={legalNavItem}
            isActive={location.pathname === legalNavItem.path}
            compact
          />
        </nav>
        <Outlet />
      </main>

      {/* Right Sidebar - Docs nav: hidden on mobile, visible from md up */}
      <aside className="hidden md:flex w-64 flex-shrink-0 flex-col min-h-0 bg-gray-50/50 border-l border-gray-200">
        <nav className="flex-1 p-4 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Documentation
          </p>
          <ul className="space-y-1">
            {docNavItems.map((item) => (
              <li key={item.name}>
                <DocNavLink item={item} isActive={location.pathname === item.path} />
              </li>
            ))}
          </ul>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-3">
            Legal
          </p>
          <ul>
            <li>
              <DocNavLink
                item={legalNavItem}
                isActive={location.pathname === legalNavItem.path}
              />
            </li>
          </ul>
        </nav>
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-amber-800">
                {user?.firstName?.[0]}{user?.lastName?.[0] || 'YA'}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {selectedSchool?.name || 'Yoga Academy'} Admin
              </p>
            </div>
          </div>
        </div>
      </aside>
    </div>
  )
}

export default DocumentationLayout
